import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clienteService, prestamoService } from '../../services/api';
import '../../styles/Clientes.css';
import { usePermisosGuard } from '../../hooks/usePermisosGuard';

const EliminarReenganche = () => {
  usePermisosGuard('prestamos', 'eliminar');
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchId, setSearchId] = useState('');
  const [prestamo, setPrestamo] = useState(null);
  const [prestamoOrigen, setPrestamoOrigen] = useState(null);
  const [confirmacion, setConfirmacion] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [prestamosPorCliente, setPrestamosPorCliente] = useState([]);

  // Obtener tenant slug de la URL
  const getTenantSlug = () => {
    const pathParts = location.pathname.split('/');
    return pathParts[1]; // Asume formato: /tenant/prestamos/eliminar-reenganche
  };

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clienteService.getClientes({ per_page: 1000 });
      setClientes(response.data.clientes || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const buscarPrestamo = async () => {
    if (!searchId) {
      setError('Ingrese un ID de préstamo');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setPrestamo(null);
      setPrestamoOrigen(null);
      setConfirmacion(false);

      const response = await prestamoService.getPrestamo(searchId);
      const prestamoData = response.data;

      if (!prestamoData.prestamo_origen_id) {
        setError('Este préstamo no es un reenganche');
        return;
      }

      setPrestamo(prestamoData);

      // Buscar el préstamo original
      const responseOrigen = await prestamoService.getPrestamo(prestamoData.prestamo_origen_id);
      setPrestamoOrigen(responseOrigen.data);

    } catch (err) {
      console.error('Error:', err);
      setError('Préstamo no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleClienteChange = async (clienteId) => {
    setClienteSeleccionado(clienteId);
    setPrestamo(null);
    setPrestamoOrigen(null);
    setPrestamosPorCliente([]);
    setConfirmacion(false);
    setError('');

    if (!clienteId) {
      return;
    }

    try {
      setLoading(true);
      const response = await prestamoService.getPrestamos({
        cliente_id: clienteId,
        per_page: 1000
      });
      // Filtrar solo préstamos que sean reenganches (tienen prestamo_origen_id)
      const reenganches = (response.data.prestamos || []).filter(p => p.prestamo_origen_id);
      setPrestamosPorCliente(reenganches);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar préstamos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionarPrestamo = async (prestamoId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setPrestamo(null);
      setPrestamoOrigen(null);
      setConfirmacion(false);

      const response = await prestamoService.getPrestamo(prestamoId);
      const prestamoData = response.data;

      if (!prestamoData.prestamo_origen_id) {
        setError('Este préstamo no es un reenganche');
        return;
      }

      setPrestamo(prestamoData);

      // Buscar el préstamo original
      const responseOrigen = await prestamoService.getPrestamo(prestamoData.prestamo_origen_id);
      setPrestamoOrigen(responseOrigen.data);

    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async () => {
    if (!confirmacion) {
      setError('Debe confirmar la eliminación');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Eliminar el préstamo reenganchado (cambiar estado a cancelado)
      await prestamoService.eliminarPrestamo(prestamo.id);

      // Restaurar el préstamo original (cambiar estado a activo)
      await prestamoService.editarPrestamo(prestamo.prestamo_origen_id, {
        estado: 'activo'
      });

      setSuccess('Reenganche eliminado exitosamente. El préstamo original ha sido restaurado.');

      setTimeout(() => {
        const tenantSlug = getTenantSlug();
        navigate(`/${tenantSlug}/prestamos/lista`);
      }, 2000);

    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al eliminar el reenganche');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return `$${(amount || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-DO');
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>❌ Eliminar Reenganche</h1>
        <button
          className="btn"
          onClick={() => {
            const tenantSlug = getTenantSlug();
            navigate(`/${tenantSlug}/prestamos`);
          }}
          style={{background: '#666'}}
        >
          ← Volver
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}
      {success && <div className="banner success">{success}</div>}

      {/* Búsqueda */}
      <div className="card" style={{marginBottom: '2rem'}}>
        <div className="card-title" style={{background: '#e74c3c'}}>
          Buscar Préstamo Reenganchado
        </div>
        <div style={{padding: '1rem'}}>
          {/* Búsqueda por ID */}
          <div style={{marginBottom: '1.5rem'}}>
            <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-end'}}>
              <div style={{flex: 1}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                  Préstamo ID
                </label>
                <input
                  type="number"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  placeholder="Ingrese el ID del préstamo"
                  className="form-control"
                />
              </div>
              <button
                onClick={buscarPrestamo}
                className="btn"
                style={{background: '#e74c3c', color: '#fff', padding: '0.75rem 2rem'}}
                disabled={loading}
              >
                Buscar por ID
              </button>
            </div>
          </div>

          {/* Separador */}
          <div style={{
            borderTop: '2px dashed #ddd',
            margin: '1.5rem 0',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff',
              padding: '0 1rem',
              color: '#666',
              fontWeight: 600
            }}>
              O
            </span>
          </div>

          {/* Búsqueda por Cliente */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
              Buscar por Cliente
            </label>
            <select
              value={clienteSeleccionado}
              onChange={(e) => handleClienteChange(e.target.value)}
              className="form-control"
              disabled={loading}
            >
              <option value="">--- Seleccione un cliente ---</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nombres} {cliente.apellidos} - {cliente.cedula}
                </option>
              ))}
            </select>
          </div>

          {/* Lista de préstamos reenganchados del cliente */}
          {prestamosPorCliente.length > 0 && (
            <div style={{marginTop: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                Reenganches del Cliente
              </label>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                {prestamosPorCliente.map(prestamoItem => (
                  <div
                    key={prestamoItem.id}
                    onClick={() => handleSeleccionarPrestamo(prestamoItem.id)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      background: prestamo?.id === prestamoItem.id ? '#ffebee' : '#fff',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (prestamo?.id !== prestamoItem.id) {
                        e.currentTarget.style.background = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (prestamo?.id !== prestamoItem.id) {
                        e.currentTarget.style.background = '#fff';
                      }
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <strong>ID: {prestamoItem.id}</strong> - {prestamoItem.modalidad}
                        <div style={{fontSize: '0.85rem', color: '#666'}}>
                          Origen: ID {prestamoItem.prestamo_origen_id}
                        </div>
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <div style={{fontWeight: 'bold', color: '#e67e22'}}>
                          ${prestamoItem.total.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                        </div>
                        <span className={`badge badge-${
                          prestamoItem.estado === 'activo' ? 'success' :
                          prestamoItem.estado === 'pagado' ? 'info' : 'warning'
                        }`}>
                          {prestamoItem.estado}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {clienteSeleccionado && prestamosPorCliente.length === 0 && !loading && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              color: '#856404'
            }}>
              Este cliente no tiene préstamos reenganchados
            </div>
          )}
        </div>
      </div>

      {/* Información del Préstamo Reenganchado */}
      {prestamo && (
        <>
          <div className="card" style={{marginBottom: '2rem'}}>
            <div className="card-title" style={{background: '#e67e22'}}>
              Préstamo Reenganchado (Actual)
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>ID</label>
                <div>{prestamo.id}</div>
              </div>

              <div className="form-group">
                <label>Cliente</label>
                <div style={{fontWeight: 'bold'}}>
                  {prestamo.cliente?.nombres} {prestamo.cliente?.apellidos}
                </div>
              </div>

              <div className="form-group">
                <label>Capital</label>
                <div>{formatMoney(prestamo.capital)}</div>
              </div>

              <div className="form-group">
                <label>Total</label>
                <div style={{fontWeight: 'bold', color: '#e67e22'}}>
                  {formatMoney(prestamo.total)}
                </div>
              </div>

              <div className="form-group">
                <label>Modalidad</label>
                <div>{prestamo.modalidad}</div>
              </div>

              <div className="form-group">
                <label>Cuotas</label>
                <div>{prestamo.cuotas}</div>
              </div>

              <div className="form-group">
                <label>Fecha Inicio</label>
                <div>{formatDate(prestamo.fecha_inicio)}</div>
              </div>

              <div className="form-group">
                <label>Estado</label>
                <div>
                  <span className={`badge badge-${prestamo.estado === 'activo' ? 'success' : 'warning'}`}>
                    {prestamo.estado}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información del Préstamo Original */}
          {prestamoOrigen && (
            <div className="card" style={{marginBottom: '2rem'}}>
              <div className="card-title" style={{background: '#95a5a6'}}>
                Préstamo Original (Será Restaurado)
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>ID</label>
                  <div>{prestamoOrigen.id}</div>
                </div>

                <div className="form-group">
                  <label>Capital</label>
                  <div>{formatMoney(prestamoOrigen.capital)}</div>
                </div>

                <div className="form-group">
                  <label>Total</label>
                  <div style={{fontWeight: 'bold'}}>
                    {formatMoney(prestamoOrigen.total)}
                  </div>
                </div>

                <div className="form-group">
                  <label>Estado Actual</label>
                  <div>
                    <span className="badge badge-warning">
                      {prestamoOrigen.estado}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Modalidad</label>
                  <div>{prestamoOrigen.modalidad}</div>
                </div>

                <div className="form-group">
                  <label>Cuotas</label>
                  <div>{prestamoOrigen.cuotas}</div>
                </div>

                <div className="form-group">
                  <label>Fecha Inicio</label>
                  <div>{formatDate(prestamoOrigen.fecha_inicio)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmación */}
          <div className="card" style={{marginBottom: '2rem', border: '2px solid #e74c3c'}}>
            <div className="card-title" style={{background: '#e74c3c'}}>
              ⚠️ Confirmación
            </div>
            <div style={{padding: '1.5rem'}}>
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1.5rem'
              }}>
                <p style={{margin: 0, color: '#856404', fontWeight: 600}}>
                  ⚠️ IMPORTANTE:
                </p>
                <ul style={{marginTop: '0.5rem', marginBottom: 0, color: '#856404'}}>
                  <li>El préstamo reenganchado (ID: {prestamo.id}) será cancelado</li>
                  <li>El préstamo original (ID: {prestamoOrigen?.id}) será restaurado a estado ACTIVO</li>
                  <li>Esta acción NO se puede deshacer</li>
                </ul>
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}>
                <input
                  type="checkbox"
                  checked={confirmacion}
                  onChange={(e) => setConfirmacion(e.target.checked)}
                  style={{width: '20px', height: '20px'}}
                />
                <span>Confirmo que deseo eliminar este reenganche y restaurar el préstamo original</span>
              </label>
            </div>
          </div>

          {/* Botones */}
          <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
            <button
              onClick={() => navigate('/prestamos')}
              className="btn"
              style={{background: '#95a5a6'}}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleEliminar}
              className="btn"
              style={{
                background: confirmacion ? '#e74c3c' : '#ccc',
                color: '#fff',
                cursor: confirmacion ? 'pointer' : 'not-allowed'
              }}
              disabled={loading || !confirmacion}
            >
              {loading ? 'Procesando...' : 'Eliminar Reenganche'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EliminarReenganche;
